import { isPlatformError } from '@agentic/contracts';
import { SkeletonRows } from '@agentic/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Navigate, useLocation } from '@tanstack/react-router';
import { z } from 'zod';
import { taskQuery } from '../../../app/queries.ts';
import { QueryError } from '../../../app/route-states.tsx';
import { CARD, PAGE } from '../../../app/shell.tsx';

/** Old episode links keep their layer (`tab`) and open drawer (`item`); `layout` is gone (20a). */
const Search = z.object({
  tab: z.string().optional().catch(undefined),
  item: z.string().optional().catch(undefined),
});

/**
 * 20a: `/tasks/$taskId` links still resolve. One read of `GET /tasks/{id}` gives the case and the activation; the
 * route then replaces itself with `/cases/$caseId?activity=<activationId>`.
 */
export const Route = createFileRoute('/_app/tasks/$taskId')({
  validateSearch: Search,
  component: TaskRedirect,
});

function TaskRedirect() {
  const { taskId } = Route.useParams();
  const { tab, item } = Route.useSearch();
  // Review 9: an old link's `#anchor` survives the redirect.
  const hash = useLocation({ select: (l) => l.hash });
  const query = useQuery({ ...taskQuery(taskId), staleTime: 0, retry: false });
  const caseId = query.data?.caseId ?? query.data?.task.caseId;
  const activity = query.data?.activationId ?? query.data?.task.activationId;
  if (caseId) {
    return (
      <Navigate
        to="/cases/$caseId"
        params={{ caseId }}
        search={{
          ...(activity ? { activity } : {}),
          ...(tab ? { tab } : {}),
          ...(item ? { item } : {}),
        }}
        replace
        {...(hash ? { hash } : {})}
      />
    );
  }
  return (
    <div className={PAGE}>
      <div className={`p-4 ${CARD}`} data-testid="task-redirect">
        {query.isPending ? (
          <SkeletonRows rows={3} label="Opening the case" />
        ) : query.isError && isPlatformError(query.error) && query.error.code === 'not_found' ? (
          <p className="text-ink-2" data-testid="task-not-found">
            Not found or not migrated yet. Look for the work on the{' '}
            <Link to="/cases" className="underline">
              case list
            </Link>
            .
          </p>
        ) : query.isError ? (
          <QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <p className="text-ink-2">This agent run belongs to no case yet. Look for it on the case list.</p>
        )}
      </div>
    </div>
  );
}
